import React, { useState } from 'react';
import { FiPlus, FiMinus, FiMove } from 'react-icons/fi';
import { BugData } from './ClosedBetaWizard';
import { AttachmentDropzone } from './AttachmentDropzone';

interface StepBugProps {
  initialData?: BugData;
  onSubmit: (data: BugData) => void;
  onBack: () => void;
  attachments: File[];
  onAttachmentsChange: (files: File[]) => void;
}

export const StepBug: React.FC<StepBugProps> = ({
  initialData,
  onSubmit,
  onBack,
  attachments,
  onAttachmentsChange
}) => {
  const [formData, setFormData] = useState<BugData>({
    type: 'BUG',
    category: initialData?.category || '',
    severity: initialData?.severity || '',
    title: initialData?.title || '',
    steps: initialData?.steps || [''],
    expected: initialData?.expected || '',
    actual: initialData?.actual || '',
    context: initialData?.context || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.severity) newErrors.severity = 'Severity is required';
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (formData.title.length > 140) newErrors.title = 'Title must be 140 characters or less';
    
    const validSteps = formData.steps.filter(step => step.trim());
    if (validSteps.length === 0) newErrors.steps = 'At least one step is required';
    
    if (!formData.expected.trim()) newErrors.expected = 'Expected behavior is required';
    if (!formData.actual.trim()) newErrors.actual = 'Actual behavior is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Filter out empty steps
      const cleanedData = {
        ...formData,
        steps: formData.steps.filter(step => step.trim())
      };
      onSubmit(cleanedData);
    }
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, '']
    }));
  };

  const removeStep = (index: number) => {
    if (formData.steps.length > 1) {
      setFormData(prev => ({
        ...prev,
        steps: prev.steps.filter((_, i) => i !== index)
      }));
    }
  };

  const updateStep = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === index ? value : step)
    }));
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < formData.steps.length) {
      const newSteps = [...formData.steps];
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      setFormData(prev => ({ ...prev, steps: newSteps }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Category *
        </label>
        <select
          value={formData.category}
          onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Select a category</option>
          <option value="UI/UX">UI/UX</option>
          <option value="Functional">Functional</option>
          <option value="Performance">Performance</option>
          <option value="Data/Sync">Data/Sync</option>
          <option value="Email/Notifications">Email/Notifications</option>
          <option value="Auth/Access">Auth/Access</option>
          <option value="Billing/Payments">Billing/Payments</option>
          <option value="Other">Other</option>
        </select>
        {errors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.category}</p>}
      </div>

      {/* Severity */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Severity *
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { value: 'P3', label: 'P3 - Low', color: 'bg-gray-100 dark:bg-gray-700' },
            { value: 'P2', label: 'P2 - Medium', color: 'bg-yellow-100 dark:bg-yellow-900' },
            { value: 'P1', label: 'P1 - High', color: 'bg-orange-100 dark:bg-orange-900' },
            { value: 'P0', label: 'P0 - Critical', color: 'bg-red-100 dark:bg-red-900' }
          ].map(severity => (
            <button
              key={severity.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, severity: severity.value }))}
              className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                formData.severity === severity.value
                  ? `${severity.color} ring-2 ring-blue-500`
                  : `${severity.color} hover:ring-1 hover:ring-gray-300`
              }`}
            >
              {severity.label}
            </button>
          ))}
        </div>
        {errors.severity && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.severity}</p>}
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Title * ({formData.title.length}/140)
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Brief summary of the bug"
          maxLength={140}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        {errors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title}</p>}
      </div>

      {/* Steps to Reproduce */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Steps to Reproduce *
        </label>
        <div className="space-y-2">
          {formData.steps.map((step, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-xs font-medium text-blue-600 dark:text-blue-400 mt-2">
                {index + 1}
              </div>
              <div className="flex-1">
                <textarea
                  value={step}
                  onChange={e => updateStep(index, e.target.value)}
                  placeholder={`Step ${index + 1}`}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => moveStep(index, 'up')}
                  disabled={index === 0}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                >
                  <FiMove className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  disabled={formData.steps.length === 1}
                  className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-50"
                >
                  <FiMinus className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addStep}
          className="mt-2 flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <FiPlus className="w-4 h-4" />
          Add Step
        </button>
        {errors.steps && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.steps}</p>}
      </div>

      {/* Expected vs Actual */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
            Expected Behavior *
          </label>
          <textarea
            value={formData.expected}
            onChange={e => setFormData(prev => ({ ...prev, expected: e.target.value }))}
            placeholder="What should happen?"
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          {errors.expected && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.expected}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
            Actual Behavior *
          </label>
          <textarea
            value={formData.actual}
            onChange={e => setFormData(prev => ({ ...prev, actual: e.target.value }))}
            placeholder="What actually happens?"
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          {errors.actual && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.actual}</p>}
        </div>
      </div>

      {/* Additional Context */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Additional Context
        </label>
        <textarea
          value={formData.context}
          onChange={e => setFormData(prev => ({ ...prev, context: e.target.value }))}
          placeholder="Any other relevant information..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Attachments */}
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          Screenshots
        </label>
        <AttachmentDropzone
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Continue to Review
        </button>
      </div>
    </form>
  );
}; 